// adminportal/src/pages/WebPortal.tsx
import React, { useState, useEffect } from 'react';
import {
  Folder,
  File,
  Upload,
  Download,
  RefreshCw,
  Home,
  ChevronLeft,
  Loader2,
  Shield,
  LogOut,
  Trash2,
} from 'lucide-react';
import { apiUrl } from '../config';
import { authenticatedFetch } from '../utils/api';
import { isAuthed, clearAuth } from '../auth';
import { useNavigate } from 'react-router-dom';
import { CognitoAuthService } from '../services/cognitoAuth';
import { usePageTitle } from '../hooks/usePageTitle';
import { loadCognitoConfig } from '../config/cognito';

interface FileItem {
  name: string;
  type: 'file' | 'directory';
  size?: number;
  modified?: string;
  path: string;
}

export default function WebPortal() {
  const navigate = useNavigate();

  // Set page title
  usePageTitle('Transfer Portal');

  // Helper function to sanitize company name for S3 folder names (matches backend logic)
  const getCompanyFolder = (companyName: string | undefined, companyId: string | undefined): string => {
    if (companyName) {
      return companyName
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-') // Replace spaces with dashes
        .replace(/[^a-z0-9-]/g, '') // Remove special characters
        .replace(/-+/g, '-') // Replace multiple dashes with single dash
        .replace(/^-|-$/g, ''); // Remove leading/trailing dashes
    }
    return companyId || 'unknown-company';
  };
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState('/'); // Start at root, will be updated after user info loads
  const [dragOver, setDragOver] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [userInfo, setUserInfo] = useState<{
    companyName: string;
    userName: string;
    companyId: string;
    userId: string;
    role: string;
    userFolderName: string;
  } | null>(null);
  const [userType, setUserType] = useState<string | null>(null);

  // Check authentication on component mount
  useEffect(() => {
    if (!isAuthed()) {
      navigate('/web-portal-login');
      return;
    }
    // Load user type from localStorage
    const storedUserType = localStorage.getItem('userType');
    setUserType(storedUserType);
    loadUserInfo();
  }, [navigate]);

  // Load files when path changes and userInfo is available
  useEffect(() => {
    console.log(
      '🔄 useEffect triggered - currentPath:',
      currentPath,
      'userInfo:',
      userInfo,
      'isAuthed:',
      isAuthed()
    );
    if (isAuthed() && userInfo) {
      // If path is '/', convert it to company folder path
      if (currentPath === '/') {
        const companyFolder = getCompanyFolder(userInfo.companyName, userInfo.companyId);
        setCurrentPath(`companies/${companyFolder}/`);
        return; // Will trigger another useEffect when path changes
      }
      loadFiles();
    }
  }, [currentPath, userInfo?.userId]); // Only depend on userId, not the entire userInfo object

  const loadUserInfo = async () => {
    try {
      // Load Cognito config first
      await loadCognitoConfig();

      // Get current user's ID from Cognito
      const accessToken = CognitoAuthService.getAccessToken();
      if (!accessToken) {
        console.error('No access token available');
        setError('No access token available. Please log in again.');
        return;
      }

      const userDetails = await CognitoAuthService.getUserDetails(accessToken);
      const userId = userDetails.username;

      if (!userId) {
        console.error('No user ID available');
        setError('No user ID available. Please log in again.');
        return;
      }

      // Get user info from API with userId parameter
      const mockResponse = await fetch(
        `${apiUrl('user/profile')}?userId=${encodeURIComponent(userId)}`
      );
      if (mockResponse.ok) {
        const userData = await mockResponse.json();
        console.log('User profile data:', userData); // Debug logging

        // Use the company ID from the API mockResponse
        const companyId = userData.companyId;
        if (!companyId) {
          console.error('No company ID found in user profile');
          throw new Error('User is not associated with any company');
        }

        // Extract email prefix for folder name (same as S3 folder creation logic)
        const emailPrefix =
          userData.email?.split('@')[0]?.toLowerCase() || userData.userId || 'unknown-user';
        const userFolderName = emailPrefix
          .replace(/[^a-z0-9-]/g, '-') // Remove special characters
          .replace(/-+/g, '-') // Replace multiple dashes with single dash
          .replace(/^-|-$/g, ''); // Remove leading/trailing dashes

        const userInfoData = {
          companyName: userData.companyName || 'Unknown Company',
          userName: userData.displayName || userData.username || 'unknown',
          companyId,
          userId: userData.userId || 'unknown-user',
          role: userData.role || 'standard-user',
          userFolderName,
        };

        setUserInfo(userInfoData);

        // Start users in their company folder so they can navigate to company folders (Inbound/Outbound)
        // and also navigate down to their personal folder if needed
        // Use the correct S3 path format: companies/{companyName}/
        const companyFolder = getCompanyFolder(userInfoData.companyName, userInfoData.companyId);
        setCurrentPath(`companies/${companyFolder}/`);
        // Load files will be triggered by the useEffect when currentPath changes
      } else {
        console.error(
          'Failed to load user profile:',
          mockResponse.status,
          await mockResponse.text()
        );
        throw new Error('Failed to load user profile');
      }
    } catch (err) {
      console.error('Error loading user info:', err);
      setError(
        `Failed to load user profile: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
      // Don't set fallback user info - let the error be displayed
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/web-portal-login');
  };

  const loadFiles = async () => {
    console.log('🔄 loadFiles() called - currentPath:', currentPath, 'userInfo:', userInfo);

    // Prevent multiple simultaneous API calls
    if (loading) {
      console.log('⏭️ loadFiles() already in progress, skipping');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (!userInfo) return;

      let fullPath: string;

      const companyFolder = getCompanyFolder(userInfo.companyName, userInfo.companyId);

      if (currentPath === '/') {
        // At root level - show company folders (user's own company only)
        fullPath = `companies/${companyFolder}`;
      } else if (currentPath.startsWith('companies/')) {
        // Full S3 path - use it directly (no leading slash)
        fullPath = currentPath.replace(/\/+/g, '/');
      } else if (currentPath.startsWith('/companies/')) {
        // Full S3 path with leading slash - remove slash and use directly
        fullPath = currentPath.replace(/^\//, '').replace(/\/+/g, '/');
      } else if (currentPath.startsWith('/users/')) {
        // User's specific folder - only allow access to own folder
        const pathParts = currentPath.split('/');
        const pathUserId = pathParts[2]; // /users/{userId}/...

        if (pathUserId === userInfo.userId) {
          // User accessing their own folder - allow it
          fullPath = `companies/${companyFolder}${currentPath}`.replace(/\/+/g, '/');
        } else {
          // Access denied - redirect to user's own folder
          fullPath = `companies/${companyFolder}/users/${userInfo.userId}`;
        }
      } else if (currentPath === '/inbound/' || currentPath === '/outbound/') {
        // Direct navigation to company folders
        fullPath = `companies/${companyFolder}${currentPath}`.replace(/\/+/g, '/');
      } else {
        // Fallback - treat as user folder
        const cleanPath = currentPath.startsWith('/') ? currentPath.slice(1) : currentPath;
        fullPath = `companies/${companyFolder}/users/${userInfo.userId}/${cleanPath}`.replace(
          /\/+/g,
          '/'
        );
      }

      console.log('Loading files from path:', fullPath); // Debug logging
      
      const apiEndpoint = `files/list?path=${encodeURIComponent(fullPath)}`;
      const fullApiUrl = `${apiUrl('files/list')}?path=${encodeURIComponent(fullPath)}`;
      console.log('🌐 API endpoint:', apiEndpoint);
      console.log('🔍 Full API URL:', fullApiUrl);

      // Call real API to get files with authentication
      const mockResponse = await authenticatedFetch(apiEndpoint);
      if (mockResponse.ok) {
        const filesData = await mockResponse.json();
        console.log('📦 Files data received:', JSON.stringify(filesData, null, 2)); // Debug logging
        console.log('📁 Files array:', filesData.files);
        console.log('🔢 Files count:', filesData.files?.length || 0);
        console.log('📊 Total files:', filesData.totalFiles);
        console.log('📂 Total folders:', filesData.totalFolders);
        console.log('🔍 Requested path:', fullPath);

        // Filter out doubled path folders on the client side as well
        // Handle different possible response structures
        const allFiles = filesData.files || filesData.items || filesData.contents || [];
        console.log('Before filtering - files count:', allFiles.length);
        console.log('All files before filtering:', JSON.stringify(allFiles, null, 2));
        
        const filteredFiles = allFiles.filter((file: FileItem) => {
          if (file.type === 'directory') {
            const pathParts = file.path.split('/').filter(Boolean);

            // Check for doubled path patterns
            for (let i = 0; i < pathParts.length - 1; i++) {
              if (pathParts[i] === pathParts[i + 1]) {
                console.log('Client-side filtering: Skipping doubled path folder:', file.path);
                return false;
              }
            }

            // Check for multiple "users" or "companies" folders
            const usersCount = pathParts.filter(part => part === 'users').length;
            const companiesCount = pathParts.filter(part => part === 'companies').length;

            if (usersCount > 1 || companiesCount > 1) {
              console.log(
                'Client-side filtering: Skipping folder with multiple users/companies:',
                file.path
              );
              return false;
            }
          }
          return true;
        });

        console.log('After filtering - files count:', filteredFiles.length);
        console.log('Filtered files:', filteredFiles);
        
        // Debug: Log each file that was filtered out
        const filteredOutPaths = new Set(filteredFiles.map((f: FileItem) => f.path));
        const filteredOut = allFiles.filter((file: FileItem) => !filteredOutPaths.has(file.path));
        if (filteredOut.length > 0) {
          console.log('⚠️ Files filtered out:', filteredOut);
          console.log('⚠️ Filtered out count:', filteredOut.length);
        }
        
        if (filteredFiles.length === 0 && allFiles.length > 0) {
          console.warn('⚠️ WARNING: All files were filtered out!', {
            totalFiles: allFiles.length,
            filteredFiles: filteredFiles.length,
            allFiles: allFiles,
          });
        }
        
        setFiles(filteredFiles);
      } else {
        const errorText = await mockResponse.text();
        console.error('❌ API Error Response:', {
          status: mockResponse.status,
          statusText: mockResponse.statusText,
          body: errorText,
        });
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || 'Unknown error' };
        }
        throw new Error(`Failed to load files: ${errorData.error || mockResponse.statusText}`);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileClick = (file: FileItem) => {
    if (file.type === 'directory') {
      // Use the full path from the API response
      // Ensure it has proper formatting
      let newPath = file.path.replace(/\/+/g, '/');
      
      // If path doesn't start with companies/, we need to construct it
      if (!newPath.startsWith('companies/')) {
        if (userInfo) {
          const companyFolder = getCompanyFolder(userInfo.companyName, userInfo.companyId);
          // If it's a relative path, prepend the full path
          if (newPath.startsWith('/')) {
            newPath = `companies/${companyFolder}${newPath}`;
          } else {
            newPath = `companies/${companyFolder}/${newPath}`;
          }
        }
      }
      
      // Ensure path ends with / for directories
      if (!newPath.endsWith('/')) {
        newPath = `${newPath}/`;
      }
      
      setCurrentPath(newPath);
      setSelectedFile(null);
    } else {
      setSelectedFile(file.path);
    }
  };

  const navigateUp = () => {
    if (!userInfo) return;
    
    const companyFolder = getCompanyFolder(userInfo.companyName, userInfo.companyId);
    const companyLevel = `companies/${companyFolder}/`;
    
    // Don't allow navigation above the company level - users should only see their own company
    if (currentPath === companyLevel || currentPath === '/') {
      return; // Already at company level, can't go up further
    }
    
    const pathParts = currentPath.split('/').filter(Boolean);
    pathParts.pop();
    let newPath = pathParts.length > 0 ? pathParts.join('/') : '/';

    // If we're in a user's personal folder, going up should take us to the company level
    // (skipping the /users/ level since users/ is at the same level as inbound/outbound)
    // Structure: companies/{company}/users/{username}/ -> companies/{company}/
    if (newPath.includes('/users/')) {
      // If we're at companies/{company}/users/, go up to companies/{company}/
      if (newPath === `companies/${companyFolder}/users`) {
        newPath = companyLevel;
      } else if (newPath.endsWith('/users')) {
        // Extract company folder and go to company level
        const parts = newPath.split('/');
        const companyIndex = parts.indexOf('companies');
        if (companyIndex >= 0 && companyIndex + 1 < parts.length) {
          newPath = `companies/${parts[companyIndex + 1]}/`;
        }
      }
    }

    // Ensure we never go above the company level
    if (!newPath.startsWith(`companies/${companyFolder}/`) && newPath !== '/') {
      newPath = companyLevel;
    }

    // Ensure S3 paths have proper formatting
    if (newPath.startsWith('companies/') && !newPath.endsWith('/')) {
      newPath = `${newPath}/`;
    }

    // Final safety check - ensure we're still within the user's company
    if (newPath !== '/' && !newPath.startsWith(`companies/${companyFolder}/`)) {
      newPath = companyLevel;
    }

    setCurrentPath(newPath);
    setSelectedFile(null);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size limit for HTTP uploads (2GB)
    const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB
    if (file.size > MAX_FILE_SIZE) {
      alert(
        `File too large! Maximum size is 2GB for HTTP uploads. Your file is ${(file.size / (1024 * 1024 * 1024)).toFixed(2)}GB`
      );
      return;
    }

    setUploading(true);
    try {
      if (!userInfo) return;

      // Use the same approach as the dual pane - use currentPath directly
      // Remove leading slash and ensure clean path
      let uploadPath = currentPath.replace(/^\//, '').replace(/\/+$/, '');

      // Helper function to sanitize company name for folder names (matches backend logic)
      function companyNameToFolderName(companyName: string): string {
        return companyName
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '-') // Replace spaces with dashes
          .replace(/[^a-z0-9-]/g, '') // Remove special characters
          .replace(/-+/g, '-') // Replace multiple dashes with single dash
          .replace(/^-|-$/g, ''); // Remove leading/trailing dashes
      }

      // If we're at root or just 'companies', use the user's base path
      if (!uploadPath || uploadPath === 'companies') {
        const companyFolder = userInfo.companyName 
          ? companyNameToFolderName(userInfo.companyName)
          : (userInfo.companyId || 'unknown-company');
        uploadPath = `companies/${companyFolder}/users/${userInfo.userId}`;
      } else {
        // Sanitize the company name in the path if it contains spaces
        // This ensures the path matches what the backend expects
        const pathParts = uploadPath.split('/');
        if (pathParts.length >= 2 && pathParts[0] === 'companies') {
          // Check if company folder name has spaces and needs sanitization
          const companyFolderInPath = pathParts[1];
          if (companyFolderInPath && companyFolderInPath.includes(' ')) {
            // Replace the company folder name with sanitized version
            const sanitizedCompanyFolder = getCompanyFolder(companyFolderInPath, undefined);
            pathParts[1] = sanitizedCompanyFolder;
            uploadPath = pathParts.join('/');
          }
        }
      }
      // If currentPath already contains a full path (starts with companies/), use it as-is
      // This prevents the nested folder creation issue

      // Check file size - use presigned URL for files > 5MB (API Gateway limit is 6MB)
      // Always use presigned URL approach - it's more reliable and handles all file sizes
      console.log('Using presigned URL upload for file:', file.name, 'size:', file.size);

      const presignedResponse = await authenticatedFetch('files/presigned-upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          filePath: uploadPath,
          fileSize: file.size,
        }),
      });

      if (!presignedResponse.ok) {
        const errorData = await presignedResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get presigned URL');
      }

      const { presignedUrl, s3Key, bucketName } = await presignedResponse.json();

      // Upload directly to S3 using presigned URL (binary-safe, no corruption)
      const s3Response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });

      if (!s3Response.ok) {
        throw new Error(`S3 upload failed: ${s3Response.status} ${s3Response.statusText}`);
      }

      // Create a mock mockResponse object for compatibility
      const mockResponse = {
        ok: true,
        status: 200,
        statusText: 'OK',
        json: async () => ({
          success: true,
          message: 'File uploaded successfully',
          s3Key,
          bucketName,
          fileName: file.name,
          fileSize: file.size,
          error: null,
        }),
      };

      if (mockResponse.ok) {
        let result;
        try {
          result = await mockResponse.json();
        } catch {
          // For presigned URL uploads, S3 returns empty mockResponse on success
          result = { success: true, message: 'File uploaded successfully' };
        }

        // Add the new file to the list
        const newFile: FileItem = {
          name: file.name,
          type: 'file',
          size: file.size,
          modified: new Date().toISOString().split('T')[0],
          path: `${currentPath}${file.name}`,
        };
        setFiles(prev => [...prev, newFile]);
      } else {
        let errorMessage = 'Upload failed';
        try {
          const errorData = await mockResponse.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // For presigned URL uploads, S3 returns XML error mockResponses
          errorMessage = `Upload failed with status ${mockResponse.status}`;
        }
        throw new Error(errorMessage);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (filePath: string) => {
    try {
      if (!userInfo) return;

      console.log('🚨 CACHE BUST TEST - DOWNLOAD FUNCTION CALLED:', {
        currentPath,
        filePath,
        userInfo: userInfo?.companyName,
        timestamp: new Date().toISOString(),
        random: Math.random(),
      });

      // filePath already contains the full S3 path from the API mockResponse
      // No need to construct it again - this was causing the duplication
      const fullPath = filePath;

      console.log('🔍 Final Download Path:', fullPath);

      // Download file from S3 via API
      const downloadUrl = `files/download?path=${encodeURIComponent(fullPath)}`;
      console.log('🔍 Download URL:', downloadUrl);

      const mockResponse = await authenticatedFetch(downloadUrl);
      if (mockResponse.ok) {
        const blob = await mockResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filePath.split('/').pop() || 'file';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error(`Download failed: ${mockResponse.statusText}`);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (filePath: string, fileName: string) => {
    if (!confirm(`Are you sure you want to delete "${fileName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      if (!userInfo) return;

      // Use the filePath directly as it already contains the full S3 path from the API
      // This matches the approach used in handleFileClick and the dual pane browser
      const fullPath = filePath;

      // Delete file/folder via API
      const mockResponse = await authenticatedFetch('files/delete', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filePath: fullPath }),
      });

      if (mockResponse.ok) {
        const result = await mockResponse.json();
        console.log('Delete result:', result);
        // Refresh the file list
        await loadFiles();
        setError(null);
      } else {
        const errorData = await mockResponse.json();
        throw new Error(errorData.error || `Delete failed: ${mockResponse.statusText}`);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !userInfo) return;

    setCreatingFolder(true);
    try {
      let fullPath: string;

      // Use company name (sanitized) for S3 path, not company ID
      const companyFolder = getCompanyFolder(userInfo.companyName, userInfo.companyId);

      if (currentPath === '/') {
        // At root level - create in user's home folder
        fullPath = `companies/${companyFolder}/users/${userInfo.userId}`;
      } else if (currentPath.startsWith('/companies/')) {
        // Full S3 path - use it directly
        fullPath = currentPath.replace(/^\//, '').replace(/\/+/g, '/');
      } else if (currentPath === '/inbound/' || currentPath === '/outbound/') {
        // Creating in company folders directly
        fullPath = `companies/${companyFolder}${currentPath}`.replace(/\/+/g, '/');
      } else {
        // Creating in user folder
        const cleanPath = currentPath.startsWith('/') ? currentPath.slice(1) : currentPath;
        fullPath = `companies/${companyFolder}/users/${userInfo.userId}/${cleanPath}`.replace(
          /\/+/g,
          '/'
        );
      }

      // Create folder via API
      const mockResponse = await authenticatedFetch('files/create-folder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: fullPath,
          folderName: newFolderName.trim(),
        }),
      });

      if (mockResponse.ok) {
        const newFolder: FileItem = {
          name: newFolderName.trim(),
          type: 'directory',
          path: `${currentPath}${newFolderName.trim()}/`,
        };

        setFiles(prev => [...prev, newFolder]);
        setNewFolderName('');
        setShowCreateFolder(false);
      } else {
        throw new Error(`Failed to create folder: ${mockResponse.statusText}`);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0 && userInfo) {
      setUploading(true);
      try {
        // Use the same approach as the dual pane - use currentPath directly
        // Remove leading slash and ensure clean path
        let uploadPath = currentPath.replace(/^\//, '').replace(/\/+$/, '');

        // If we're at root, use the user's base path
        if (!uploadPath || uploadPath === 'companies') {
          const companyFolder = getCompanyFolder(userInfo.companyName, userInfo.companyId);
          uploadPath = `companies/${companyFolder}/users/${userInfo.userId}`;
        }

        // Upload multiple files via API
        for (const file of droppedFiles) {
          // Check file size - use presigned URL for files > 5MB (API Gateway limit is 6MB)
          const FILE_SIZE_LIMIT = 5 * 1024 * 1024; // 5MB

          let mockResponse;
          if (file.size > FILE_SIZE_LIMIT) {
            // Use presigned URL for large files
            console.log('File is large, using presigned URL upload:', file.size);

            const presignedResponse = await authenticatedFetch('files/presigned-upload', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                fileName: file.name,
                fileType: file.type,
                filePath: uploadPath,
                fileSize: file.size,
              }),
            });

            if (!presignedResponse.ok) {
              throw new Error('Failed to get presigned URL');
            }

            const { presignedUrl } = await presignedResponse.json();

            // Upload directly to S3 using presigned URL
            mockResponse = await fetch(presignedUrl, {
              method: 'PUT',
              body: file,
              headers: {
                'Content-Type': file.type,
              },
            });
          } else {
            // Use regular API upload for small files
            const formData = new FormData();
            formData.append('file', file);
            formData.append('path', uploadPath);

            mockResponse = await authenticatedFetch('files/upload', {
              method: 'POST',
              body: formData,
            });
          }

          if (mockResponse.ok) {
            const newFile: FileItem = {
              name: file.name,
              type: 'file',
              size: file.size,
              modified: new Date().toISOString().split('T')[0],
              path: `${currentPath}${file.name}`,
            };
            setFiles(prev => [...prev, newFile]);
          } else {
            throw new Error(`Failed to upload ${file.name}: ${mockResponse.statusText}`);
          }
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setUploading(false);
      }
    }
  };

  const FileIcon = ({ file }: { file: FileItem }) => {
    if (file.type === 'directory') {
      return <Folder className='w-5 h-5 text-blue-400' />;
    }
    return <File className='w-5 h-5 text-slate-400' />;
  };

  return (
    <div className='min-h-screen bg-slate-900 text-white'>
      {/* Header */}
      <header className='bg-slate-800 border-b border-white/10'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center py-6'>
            <div className='flex items-center'>
              <Shield className='w-8 h-8 text-blue-400 mr-3' />
              <div>
                <h1 className='text-2xl font-bold text-white'>{userInfo?.companyName || 'Transfer Portal'}</h1>
                <p className='text-slate-400'>
                  {userInfo ? `${userInfo.companyName} - ${userInfo.userName}` : 'Loading...'}
                </p>
              </div>
            </div>
            <div className='flex items-center space-x-4'>
              {/* Admin Portal Button - Only show for admin users */}
              {userType === 'admin' && (
                <button
                  onClick={() => navigate('/admin-dashboard')}
                  className='flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors'
                >
                  <Shield className='w-4 h-4 mr-2' />
                  Admin Portal
                </button>
              )}
              <button
                onClick={handleLogout}
                className='flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors'
              >
                <LogOut className='w-4 h-4 mr-2' />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Breadcrumbs */}
      <div className='bg-slate-800/50 border-b border-white/10'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3'>
          <div className='flex items-center gap-2 text-sm'>
            <span className='text-slate-400'>Current Path:</span>
            <div className='flex items-center gap-1 flex-wrap'>
              {/* Helper function to generate breadcrumbs from path */}
              {(() => {
                if (!userInfo) {
                  return null;
                }
                
                const companyFolder = getCompanyFolder(userInfo.companyName, userInfo.companyId);
                
                // Define company folder path once for reuse
                const companyFolderPath = `companies/${companyFolder}/`;
                
                if (currentPath === '/' || currentPath === companyFolderPath) {
                  // At company level - just show company name
                  return (
                    <button
                      onClick={() => {
                        setCurrentPath(companyFolderPath);
                        setSelectedFile(null);
                      }}
                      className='px-2 py-1 rounded bg-blue-600 text-white transition-colors'
                    >
                      {userInfo.companyName || 'Company'}
                    </button>
                  );
                }
                
                // Parse the full path - handle both full S3 paths and relative paths
                let cleanPath = currentPath.replace(/^\//, '').replace(/\/+$/, '');
                
                // If path starts with companies/, extract the relevant parts
                let pathParts: string[] = [];
                if (cleanPath.startsWith('companies/')) {
                  // Remove companies/ prefix and split
                  const withoutCompanies = cleanPath.replace(/^companies\//, '');
                  pathParts = withoutCompanies.split('/').filter(Boolean);
                  
                  // Normalize the company folder name in path (convert dashes back to spaces for display)
                  // But keep dashes in the actual path for S3 queries
                  if (pathParts.length > 0 && pathParts[0] === companyFolder) {
                    // Path is already normalized, keep it as is
                  } else if (pathParts.length > 0) {
                    // Path might have spaces or different format - normalize it
                    const pathCompanyName = pathParts[0];
                    const sanitizedPathCompany = getCompanyFolder(pathCompanyName, undefined);
                    if (sanitizedPathCompany === companyFolder && pathCompanyName !== companyFolder) {
                      // Replace with normalized version
                      pathParts[0] = companyFolder;
                    }
                  }
                } else {
                  // Relative path - split directly
                  pathParts = cleanPath.split('/').filter(Boolean);
                }
                
                // Build breadcrumbs
                const breadcrumbs = [];
                
                // Always start with company name (display original name, but use sanitized for paths)
                breadcrumbs.push(
                  <button
                    key='company'
                    onClick={() => {
                      setCurrentPath(companyFolderPath);
                      setSelectedFile(null);
                    }}
                    className='px-2 py-1 rounded text-slate-300 hover:bg-slate-700 transition-colors'
                  >
                    {userInfo.companyName || 'Company'}
                  </button>
                );
                
                // Add path segments
                let accumulatedPath = '';
                pathParts.forEach((part, index) => {
                  // Build the accumulated path for navigation
                  if (accumulatedPath) {
                    accumulatedPath = `${accumulatedPath}/${part}`;
                  } else {
                    accumulatedPath = part;
                  }
                  
                  const isLast = index === pathParts.length - 1;
                  let displayName = part;
                  
                  // Special handling for display names
                  if (part === 'users') {
                    displayName = 'My Files';
                  } else if (part === userInfo.userFolderName || part === userInfo.userId) {
                    displayName = userInfo.userName || part;
                  } else if (part.toLowerCase() === 'inbound') {
                    displayName = 'Inbound';
                  } else if (part.toLowerCase() === 'outbound') {
                    displayName = 'Outbound';
                  } else if (part.toLowerCase() === 'quarantine') {
                    displayName = 'Quarantine';
                  } else if (part === companyFolder) {
                    // If this is the company folder segment, show the original company name
                    displayName = userInfo.companyName || part;
                  }
                  
                  breadcrumbs.push(
                    <ChevronLeft key={`chevron-${index}`} className='w-3 h-3 text-slate-500' />
                  );
                  
                  breadcrumbs.push(
                    <button
                      key={index}
                      onClick={() => {
                        // Reconstruct the full S3 path for navigation (use sanitized company folder)
                        const fullPath = `companies/${companyFolder}/${accumulatedPath}/`.replace(/\/+/g, '/');
                        setCurrentPath(fullPath);
                        setSelectedFile(null);
                      }}
                      className={`px-2 py-1 rounded transition-colors ${
                        isLast
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      {displayName}
                    </button>
                  );
                });
                
                return breadcrumbs;
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className='bg-slate-800/30 border-b border-white/10'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3'>
          <div className='flex items-center gap-4'>
            <button
              onClick={() => {
                if (userInfo) {
                  const companyFolder = getCompanyFolder(userInfo.companyName, userInfo.companyId);
                  setCurrentPath(`companies/${companyFolder}/users/${userInfo.userFolderName}/`);
                  setSelectedFile(null);
                }
              }}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                userInfo &&
                (() => {
                  const companyFolder = getCompanyFolder(userInfo.companyName, userInfo.companyId);
                  return currentPath.startsWith(`companies/${companyFolder}/users/${userInfo.userFolderName}/`);
                })()
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <Home className='w-4 h-4' />
              My Files
            </button>
            <button
              onClick={() => {
                if (userInfo) {
                  const companyFolder = getCompanyFolder(userInfo.companyName, userInfo.companyId);
                  setCurrentPath(`companies/${companyFolder}/`);
                  setSelectedFile(null);
                }
              }}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                userInfo &&
                (() => {
                  const companyFolder = getCompanyFolder(userInfo.companyName, userInfo.companyId);
                  return currentPath === `companies/${companyFolder}/` ||
                    (currentPath.startsWith(`companies/${companyFolder}/`) && !currentPath.includes('/users/'));
                })()
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <Folder className='w-4 h-4' />
              Company Folders
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4'>
          <div className='bg-red-500/10 border border-red-500/20 rounded-lg p-3'>
            <p className='text-red-400 text-sm'>{error}</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <div className='bg-slate-800 rounded-lg border border-white/10'>
          {/* File Browser Header */}
          <div className='p-4 border-b border-white/10'>
            <div className='flex items-center justify-between mb-2'>
              <h3 className='font-medium text-white'>File Browser</h3>
              <div className='flex items-center gap-2'>
                <button
                  onClick={() => setShowCreateFolder(true)}
                  className='p-2 hover:bg-slate-700 rounded transition-colors'
                  title='Create folder'
                >
                  <Folder className='w-4 h-4 text-slate-400' />
                </button>
                <input
                  type='file'
                  id='file-upload'
                  onChange={handleFileUpload}
                  className='hidden'
                  disabled={uploading}
                  multiple
                  aria-label='Upload files'
                />
                <label
                  htmlFor='file-upload'
                  className={`p-2 hover:bg-slate-700 rounded cursor-pointer transition-colors ${
                    uploading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title='Upload files'
                >
                  <Upload className='w-4 h-4 text-slate-400' />
                </label>
                <button
                  onClick={navigateUp}
                  disabled={
                    !userInfo ||
                    currentPath === '/' ||
                    (() => {
                      if (!userInfo) return false;
                      const companyFolder = getCompanyFolder(userInfo.companyName, userInfo.companyId);
                      return currentPath === `companies/${companyFolder}/`;
                    })()
                  }
                  className='p-2 hover:bg-slate-700 rounded disabled:opacity-50 disabled:cursor-not-allowed'
                  title='Go up one level'
                >
                  <ChevronLeft className='w-4 h-4 text-slate-400' />
                </button>
                <button
                  onClick={loadFiles}
                  className='p-2 hover:bg-slate-700 rounded'
                  title='Refresh'
                >
                  <RefreshCw className='w-4 h-4 text-slate-400' />
                </button>
              </div>
            </div>
            <div className='text-sm text-slate-400 font-mono'>
              {(() => {
                // Display the actual S3 path being used
                if (!userInfo) return currentPath;
                const companyFolder = getCompanyFolder(userInfo.companyName, userInfo.companyId);
                
                if (currentPath === '/') {
                  return `companies/${companyFolder}/`;
                } else if (currentPath.startsWith('companies/')) {
                  return currentPath.replace(/\/+/g, '/');
                } else if (currentPath.startsWith('/companies/')) {
                  return currentPath.replace(/^\//, '').replace(/\/+/g, '/');
                } else {
                  // Reconstruct the full path
                  const cleanPath = currentPath.replace(/^\//, '').replace(/\/+$/, '');
                  return `companies/${companyFolder}/${cleanPath}/`.replace(/\/+/g, '/');
                }
              })()}
            </div>
          </div>

          {/* File List */}
          <div className='p-4'>
            {loading ? (
              <div className='flex items-center justify-center py-8'>
                <Loader2 className='w-6 h-6 animate-spin text-blue-400' />
              </div>
            ) : (
              <div
                className={`space-y-1 min-h-[400px] ${
                  dragOver ? 'bg-blue-500/10 border-2 border-dashed border-blue-500' : ''
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {files.map(file => (
                  <div
                    key={file.path}
                    onClick={() => handleFileClick(file)}
                    className={`flex items-center gap-3 p-3 rounded cursor-pointer transition-colors ${
                      selectedFile === file.path
                        ? 'bg-blue-600/20 border border-blue-500/30'
                        : 'hover:bg-slate-700/50'
                    }`}
                  >
                    <FileIcon file={file} />
                    <span className='text-white flex-1'>{file.name}</span>
                    <div className='flex items-center gap-2'>
                      {file.size && (
                        <span className='text-xs text-slate-400'>
                          {file.size > 1024 * 1024
                            ? `${(file.size / (1024 * 1024)).toFixed(1)}MB`
                            : `${(file.size / 1024).toFixed(1)}KB`}
                        </span>
                      )}
                      {file.type === 'file' && (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleDownload(file.path);
                          }}
                          className='p-1 hover:bg-slate-600 rounded transition-colors'
                          title='Download file'
                        >
                          <Download className='w-3 h-3 text-green-400' />
                        </button>
                      )}
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleDelete(file.path, file.name);
                        }}
                        className='p-1 hover:bg-slate-600 rounded transition-colors'
                        title={`Delete ${file.type === 'file' ? 'file' : 'folder'}`}
                      >
                        <Trash2 className='w-3 h-3 text-red-400' />
                      </button>
                    </div>
                  </div>
                ))}
                {files.length === 0 && (
                  <div className='text-center py-8 text-slate-400'>
                    {dragOver ? 'Drop files here to upload' : 'No files found'}
                  </div>
                )}
              </div>
            )}

            {uploading && (
              <div className='text-center py-4'>
                <Loader2 className='w-4 h-4 animate-spin text-green-400 mx-auto mb-2' />
                <span className='text-xs text-green-400'>Uploading files...</span>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <div className='bg-slate-800 border border-white/10 rounded-lg p-6 w-full max-w-md mx-4'>
            <h3 className='text-lg font-semibold text-white mb-4'>Create New Folder</h3>
            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>Folder Name</label>
                <input
                  type='text'
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
                  placeholder='Enter folder name'
                  autoFocus
                />
              </div>
              <div className='flex justify-end space-x-3'>
                <button
                  onClick={() => {
                    setShowCreateFolder(false);
                    setNewFolderName('');
                  }}
                  className='px-4 py-2 text-slate-300 hover:text-white transition-colors'
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim() || creatingFolder}
                  className='px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors'
                >
                  {creatingFolder ? (
                    <div className='flex items-center gap-2'>
                      <Loader2 className='w-4 h-4 animate-spin' />
                      Creating...
                    </div>
                  ) : (
                    'Create Folder'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className='bg-slate-800 border-t border-white/10 mt-16'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
          <div className='text-center text-slate-400'>
            <p>&copy; 2025 {userInfo?.companyName || 'NGHS'}. All rights reserved.</p>
            <p className='mt-2 text-sm'>HIPAA-compliant file transfer solutions</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
